"use client";

import React from "react";
import { Button } from "../common/button";

export interface PricingTier {
  title: string;
  priceRange: string;
  priceSuffix?: string;
  description: string;
  features: string[];
  ctaText: string;
  onCtaClick?: () => void;
  ctaVariant?: "default" | "outline";
  isPopular?: boolean;
  popularLabel?: string;
}

export interface PricingSectionProps {
  title?: string;
  subtitle?: string;
  tiers?: PricingTier[];
}

export function PricingSection({
  title = "Therapy that fits your budget",
  subtitle = "We have two types of therapists to choose from",
  tiers = [
    {
      title: "Resident therapists",
      priceRange: "$45 - $115",
      priceSuffix: "/session",
      description: "Master's level therapists completing their supervised training",
      features: [
        "Perfect for anxiety & depression",
        "Supervised by licensed therapists",
        "Most affordable option"
      ],
      ctaText: "Learn more",
      ctaVariant: "outline" as const
    },
    {
      title: "Associate therapists",
      priceRange: "$125 - $200",
      priceSuffix: "/session",
      description: "Fully licensed therapists with years of experience",
      features: [
        "Complex issues & trauma",
        "Specialized certifications",
        "Extensive experience"
      ],
      ctaText: "Get matched",
      ctaVariant: "default" as const,
      isPopular: true,
      popularLabel: "Popular"
    }
  ]
}: PricingSectionProps) {
  return (
    <section className="bg-gray-50 py-16 md:py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">{title}</h2>
          <p className="text-lg md:text-xl text-gray-600">{subtitle}</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
          {tiers.map((tier, index) => (
            <div 
              key={index} 
              className={`bg-white rounded-lg p-6 md:p-8 shadow-sm ${
                tier.isPopular ? 'border-2 border-primary-500' : ''
              }`}
            >
              {tier.isPopular && (
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl md:text-2xl font-bold">{tier.title}</h3>
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                    {tier.popularLabel}
                  </span>
                </div>
              )}
              {!tier.isPopular && (
                <h3 className="text-xl md:text-2xl font-bold mb-2">{tier.title}</h3>
              )}
              
              <p className="text-2xl md:text-3xl font-bold text-primary-600 mb-3 md:mb-4">
                {tier.priceRange}
                {tier.priceSuffix && (
                  <span className="text-sm md:text-base text-gray-600 font-normal">
                    {tier.priceSuffix}
                  </span>
                )}
              </p>
              
              <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
                {tier.description}
              </p>
              
              <ul className="space-y-2 mb-6 md:mb-8">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center gap-2 text-sm md:text-base">
                    <svg 
                      className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M5 13l4 4L19 7" 
                      />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                variant={tier.ctaVariant as any} 
                className="w-full"
                onClick={tier.onCtaClick}
              >
                {tier.ctaText}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}